import { enableAllPlugins, produce } from 'immer';
import forEach from 'lodash/forEach';
import includes from 'lodash/includes';
import type { IntlShape } from 'react-intl';
import type { Dispatch } from 'react';
import axios, { AxiosError } from 'axios';
import type { FormContent } from '../../../../types/form/Form';
import FileRequestAPI from '../../../../api/FileRequest';
import FormApi from '../../../../api/Form';

import {
  ELEMENT_DESCRIPTION_ID,
  ELEMENT_EMAIL_ID,
  FILE_REQUEST_STATUS,
  FORM_TYPE_FILE_REQUEST,
  JOI_ERROR,
  MODE,
  REGEX_UNICODE_BMP_VALIDATION,
  STATUS_CODE,
  TYPE_UPLOAD_FIELD,
} from '../../../../constants';
import {
  ACTION_INJECT_JOI_VALIDATION_ERRORS,
  ACTION_MISMATCHED_VERSION,
  ACTION_NOTIFY_METADATA_TEMPLATE_ERROR,
  ACTION_NOTIFY_UNSUPPORTED_CHARACTERS_ERROR,
  ACTION_TOGGLE_FILE_REQUEST_ACTIVITY,
  ADMIN_BRAND_COLOR,
  ADMIN_COLOR_LOGO,
  FORMS_BUILDER_PREVIEW_WINDOW,
} from '../builder-constant';

import type {
  Element,
  MetadataFieldElement,
} from '../../../../types/form/Element';

import {
  removeField,
  removeUploadFieldFromMetadata,
  setDescriptionFieldVisible,
  setEmailFieldVisible,
} from '../../../new-file-request-builder/FormElementMaker';
import selector from '../builder-selector';
import type { State, BuilderAction } from '../builder-type';
import type { FolderInfo } from '../../../../types/form/FolderInfo';
import type { JoiError } from '../validator/joi-translator/joi-type';
import { CREATE_SAVE_ACTIONS } from './create-save-action/create-save-action-type';

type ErrorInfo = {
  versionMismatch?: boolean;
  validationError?: boolean;
  folderInfoById?: Record<string, FolderInfo>;
  hasJoiError?: boolean;
  responseError?: boolean;
};

// Enable all immer plugins
enableAllPlugins();

export const actionToggleFileRequestActivity = async (
  dispatch: Dispatch<BuilderAction>,
  state: State,
) => {
  const { workingFileRequest, savedFileRequest } = state;
  const status =
    workingFileRequest.status === FILE_REQUEST_STATUS.ACTIVE
      ? FILE_REQUEST_STATUS.INACTIVE
      : FILE_REQUEST_STATUS.ACTIVE;
  const newFileRequest = {
    ...workingFileRequest,
    status,
  };
  const fileRequest = await FileRequestAPI.updateFileRequest(
    savedFileRequest,
    newFileRequest,
  );
  dispatch({
    type: ACTION_TOGGLE_FILE_REQUEST_ACTIVITY,
    fileRequest,
  });
};

const hasSpecialChars = (text: string): boolean => {
  return REGEX_UNICODE_BMP_VALIDATION.test(text);
};

export const processContentOnSave = (state: State) => {
  const content = selector.getFormContent(state);

  return produce(content, draft => {
    // Exclude the logo image data, this is hydrated in file-request-web
    delete draft?.theme?.logo?.value;

    // Add all upload fields to metadata fields for File Requests
    if (state.workingFileRequest.form.formType === FORM_TYPE_FILE_REQUEST) {
      const uploadFieldIds = selector.getUploadFieldIds(state);
      const metadataFieldIds = selector.getMetadataFieldIds(state);

      forEach(metadataFieldIds, metadataFieldId => {
        draft.components = {
          ...draft.components,
          [metadataFieldId]: {
            ...draft.components[metadataFieldId],
            uploadFieldIds,
          } as MetadataFieldElement,
        };
      });
    }

    // check for errors in components and throw an error if any are present
    forEach(draft.components, component => {
      if (component.error) {
        throw new Error('Error in at least one form field');
      }
    });
  });
};

export const processContentOnCreateForm = (state: State) => {
  const formContent = processContentOnSave(state);
  return produce(formContent, draft => {
    // JOI validation requires content to have an explicit type
    draft.type = MODE.FORM;
  });
};

export const getLogoFileToUpload = (state: State): File | null => {
  const file = selector.getUploadLogoModalFile(state);
  const key = state.workingFileRequest.form.content?.theme?.logo?.key;
  /* If the selected logo is an uploaded file, then return that file */
  if (
    file &&
    key &&
    key !== '' &&
    key !== ADMIN_BRAND_COLOR &&
    key !== ADMIN_COLOR_LOGO
  ) {
    return file;
  }
  return null;
};

export const actionVerifyValidMetadataTemplateState =
  (dispatch: Dispatch<BuilderAction>, state: State) => async () => {
    if (state.metadataTemplateError) {
      dispatch({ type: ACTION_NOTIFY_METADATA_TEMPLATE_ERROR });
      return false;
    }
    return true;
  };

export const actionVerifyFormContainsValidCharacters =
  (dispatch: Dispatch<BuilderAction>, state: State) => async () => {
    const formContent = selector.getFormContent(state);
    if (hasSpecialChars(JSON.stringify(formContent))) {
      dispatch({ type: ACTION_NOTIFY_UNSUPPORTED_CHARACTERS_ERROR });
      return false;
    }
    return true;
  };

export const actionInjectValidationError = (
  dispatch: Dispatch<BuilderAction>,
  errorDetails: JoiError[],
  formContent: FormContent,
) => {
  dispatch({
    type: ACTION_INJECT_JOI_VALIDATION_ERRORS,
    errors: errorDetails,
    formContent,
  });
};

export const processSaveFormErrorResponse = (
  error: AxiosError<{
    message: string;
    type?: string;
    folderInfoById?: Record<string, FolderInfo>;
  }>,
) => {
  const errorCode =
    error?.response?.status || STATUS_CODE.INTERNAL_SERVER_ERROR;

  // handle joi validation errors
  let hasJoiError = false;

  const joiMessage = error?.response?.data?.message;
  const errorType = error?.response?.data?.type;

  if (joiMessage === JOI_ERROR.MESSAGE && errorType === JOI_ERROR.VALIDATION) {
    hasJoiError = true;
  }

  const errorInfo = {
    versionMismatch: false,
    validationError: false,
    folderInfoById: {},
    hasJoiError,
    responseError: false,
  };

  // Handle upload field folder validation errors
  if (errorCode === STATUS_CODE.UNAUTHORIZED) {
    errorInfo.validationError = true;
    errorInfo.folderInfoById = error?.response?.data?.folderInfoById || {};
  }
  // Handle version mismatch
  else if (errorCode === STATUS_CODE.CONFLICT) {
    errorInfo.versionMismatch = true;
  }
  // Handle all other errors
  else if (!hasJoiError) {
    errorInfo.responseError = true;
  }

  return errorInfo;
};

// The returned promise resolves to a boolean indicating whether the save was successful
export const actionSave =
  (dispatch: Dispatch<BuilderAction>, state: State) =>
  async (): Promise<boolean> => {
    // TODO - Save only if change detected
    const fileRequestId = selector.getFileRequestId(state);
    const formVersionId = selector.getFormVersionId(state);
    let errorInfo: ErrorInfo = {};
    // Only allow saving when not in an error state

    const isMetadataTemplateValid =
      await actionVerifyValidMetadataTemplateState(dispatch, state)();

    if (!isMetadataTemplateValid) {
      return false;
    }

    const formHasUnsupportedCharacters =
      await actionVerifyFormContainsValidCharacters(dispatch, state)();

    if (!formHasUnsupportedCharacters) {
      return false;
    }

    let filteredContent;
    try {
      filteredContent = processContentOnSave(state);
    } catch (e: unknown) {
      dispatch({
        type: CREATE_SAVE_ACTIONS.VALIDATION_ERROR_ON_SAVE,
        folderInfoById: {},
      });
      return false;
    }

    const file = getLogoFileToUpload(state);
    let errorResponse;
    try {
      await FormApi.postFormVersion(
        formVersionId,
        fileRequestId,
        filteredContent,
        file,
      );
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        errorResponse = error.response;
        errorInfo = processSaveFormErrorResponse(error);
      }
    }

    const {
      versionMismatch,
      validationError,
      folderInfoById,
      hasJoiError,
      responseError,
    } = errorInfo;

    if (hasJoiError) {
      actionInjectValidationError(
        dispatch,
        errorResponse?.data?.details,
        filteredContent,
      );

      return false;
    }

    if (validationError) {
      // update folderInfoById with the folderInfo from the error response
      dispatch({
        type: CREATE_SAVE_ACTIONS.VALIDATION_ERROR_ON_SAVE,
        folderInfoById: folderInfoById ?? {},
      });
      return false;
    }

    if (responseError) {
      dispatch({
        type: CREATE_SAVE_ACTIONS.RESPONSE_ERROR_ON_SAVE,
      });
      return false;
    }

    const fileRequest = await FileRequestAPI.getFileRequest(fileRequestId);
    // TODO - Handle save failures?

    dispatch({
      type: versionMismatch
        ? ACTION_MISMATCHED_VERSION
        : CREATE_SAVE_ACTIONS.SAVE_COMPLETED,
      fileRequest,
    });
    // Reactivate file request if previously deactivated due to error
    if (
      !versionMismatch &&
      state.deactivatedBadFileRequest &&
      fileRequest.status === FILE_REQUEST_STATUS.INACTIVE
    ) {
      // Update state to current version
      state.workingFileRequest = fileRequest;
      state.savedFileRequest = fileRequest;
      await actionToggleFileRequestActivity(dispatch, state);
    }
    return !versionMismatch;
  };

// The returned promise resolves to a string indicating whether the creation was successful
export const actionCreateFormAndSave =
  (dispatch: Dispatch<BuilderAction>, state: State) => async () => {
    let errorInfo: ErrorInfo = {};
    // Only allow saving when not in an error state
    const isMetadataTemplateValid =
      await actionVerifyValidMetadataTemplateState(dispatch, state)();
    if (!isMetadataTemplateValid) {
      return null;
    }

    const formHasUnsupportedCharacters =
      await actionVerifyFormContainsValidCharacters(dispatch, state)();

    if (!formHasUnsupportedCharacters) {
      return null;
    }

    const file = getLogoFileToUpload(state);
    let filteredContent;
    try {
      filteredContent = processContentOnCreateForm(state);
    } catch (e: unknown) {
      dispatch({
        type: CREATE_SAVE_ACTIONS.VALIDATION_ERROR_ON_SAVE,
        folderInfoById: {},
      });
      return e;
    }

    // BFF updates fileRequest object with form content and id
    // on save failure, BFF deletes the form and throws an error
    let fileRequest;
    let errorResponse;
    try {
      fileRequest = await FormApi.postForm(filteredContent, file);
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        errorResponse = error.response;
        errorInfo = processSaveFormErrorResponse(error);
      }
    }

    // new forms will not have a version mismatch
    const { validationError, folderInfoById, hasJoiError, responseError } =
      errorInfo;
    if (hasJoiError) {
      actionInjectValidationError(
        dispatch,
        errorResponse?.data?.details,
        filteredContent,
      );
      return null;
    }

    if (validationError) {
      // update folderInfoById with the folderInfo from the error response
      dispatch({
        type: CREATE_SAVE_ACTIONS.VALIDATION_ERROR_ON_SAVE,
        folderInfoById: folderInfoById ?? {},
      });
      return null;
    }

    if (responseError || !fileRequest) {
      dispatch({
        type: CREATE_SAVE_ACTIONS.RESPONSE_ERROR_ON_SAVE,
      });
      return null;
    }

    const fileRequestId = fileRequest.id;
    const fetchedFileRequest =
      await FileRequestAPI.getFileRequest(fileRequestId);

    dispatch({
      type: CREATE_SAVE_ACTIONS.SAVE_COMPLETED,
      fileRequest: fetchedFileRequest,
    });
    return fileRequestId;
  };

export const removeFieldFromFormContent = (
  state: State,
  component: Element,
  intl: IntlShape,
) => {
  const { id, type } = component;

  let formContent = selector.getFormContent(state);
  if (includes([ELEMENT_EMAIL_ID, ELEMENT_DESCRIPTION_ID], id)) {
    if (id === ELEMENT_EMAIL_ID) {
      const { updatedFormContent } = setEmailFieldVisible(
        formContent,
        false,
        intl,
        state.workingFileRequest.form.formType,
      );
      formContent = updatedFormContent;
    }
    if (id === ELEMENT_DESCRIPTION_ID) {
      formContent = setDescriptionFieldVisible(formContent, false, intl);
    }
  } else {
    if (type === TYPE_UPLOAD_FIELD) {
      formContent = removeUploadFieldFromMetadata(id, formContent);
    }
    formContent = removeField(id, formContent);
  }
  return formContent;
};

export function openPreviewWindow(
  type: string | null | undefined,
  workingFileRequestId: string | null | undefined,
  state: State,
): void {
  const newWindowReference = window.open('', FORMS_BUILDER_PREVIEW_WINDOW);
  if (!newWindowReference) {
    return;
  }

  if (type === MODE.FORM && workingFileRequestId) {
    newWindowReference.location =
      selector.getFormPreviewUrl(workingFileRequestId);
  } else {
    newWindowReference.location = selector.getPreviewUrl(state);
  }

  newWindowReference.focus();
}

export function recordBuilderHeaderAnalytics(state: State, target: string) {
  const { resin, resinEvent } = state;

  resin.recordAction({
    ...resinEvent,
    component: 'formbuilderheader',
    target,
    data: {
      file_request_url_id: selector.getFileRequestUrlId(state),
      form_name: selector.getTitle(state),
      form_desc: selector.getDescription(state),
      num_upload_zones: selector.getNumUploadZones(state),
      num_metadata_fields: selector.getNumMetadataFields(state),
    },
  });
}
